import React, { useMemo } from 'react';
import { sanitizeHtml } from '../../Sanitizer.jsx';
import { getActions, getBlocks, getImageUrl, toStyle } from '../shared/templateSectionUtils.jsx';

const scopeCss = (css = '', scopeId = '') => {
  const root = `#${scopeId}`;
  // Selectors that should mean "the section root itself" so authors can style the whole
  // section (overall background / font / alignment) without knowing the generated id.
  const ROOT_ALIASES = new Set(['&', ':root', ':host', 'body', 'html', 'section', ':scope']);
  return String(css || '')
    .split('}')
    .map((rule) => {
      const parts = rule.split('{');
      if (parts.length < 2) return '';
      const selector = parts[0].trim();
      const body = parts.slice(1).join('{').trim();
      if (!selector || !body) return '';
      if (selector.startsWith('@')) return `${selector}{${body}}`;
      return selector
        .split(',')
        .map((item) => {
          const trimmed = item.trim();
          if (!trimmed) return '';
          if (ROOT_ALIASES.has(trimmed)) return root;          // whole section
          if (trimmed.startsWith('&')) return `${root}${trimmed.slice(1)}`; // &:hover, &.x
          if (trimmed.startsWith(root)) return trimmed;        // already scoped
          return `${root} ${trimmed}`;                          // descendant
        })
        .filter(Boolean)
        .join(', ') + ` { ${body} }`;
    })
    .filter(Boolean)
    .join('\n');
};

const getVisible = (items = []) => (Array.isArray(items) ? items.filter((item) => item?.visible !== false) : []);

const CustomButton = ({ action, theme = {} }) => {
  if (!action?.label) return null;
  const outline = ['outline', 'ghost', 'secondary'].includes(action.variant || action.style);
  const color = action.color || theme.primary || '#07111f';
  return (
    <a
      href={action.url || action.route || '#'}
      target={action.newTab ? '_blank' : undefined}
      rel="noreferrer noopener"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: action.size === 'small' ? 36 : action.size === 'large' ? 52 : theme.buttonHeight || 44,
        padding: action.size === 'small' ? '0 16px' : '0 24px',
        borderRadius: action.radius || theme.buttonRadius || 999,
        border: `1px solid ${outline ? color : 'transparent'}`,
        background: outline ? 'transparent' : color,
        color: action.textColor || (outline ? color : '#ffffff'),
        textDecoration: 'none',
        fontWeight: 800
      }}
    >
      {action.label}
    </a>
  );
};

const CustomField = ({ item, theme = {} }) => {
  const type = item.type === 'dropdown' || item.type === 'select' ? 'select' : item.type === 'textarea' ? 'textarea' : 'input';
  const commonStyle = {
    width: '100%',
    minHeight: 44,
    border: `1px solid ${theme.borderColor || '#d5d8dd'}`,
    borderRadius: theme.inputRadius || 12,
    background: '#ffffff',
    padding: '0 12px'
  };
  return (
    <label style={{ display: 'grid', gap: 8, fontWeight: 700, fontSize: 13 }}>
      {item.label || 'Field'}
      {type === 'select' ? (
        <select style={commonStyle} defaultValue="">
          <option value="">{item.placeholder || 'Choose an option'}</option>
          {(Array.isArray(item.options) ? item.options : Array.isArray(item.features) ? item.features : []).map((option, index) => (
            <option key={`${option}-${index}`} value={String(option)}>{String(option)}</option>
          ))}
        </select>
      ) : type === 'textarea' ? (
        <textarea rows={item.rows || 4} style={{ ...commonStyle, paddingTop: 12, minHeight: 110 }} placeholder={item.placeholder || ''} />
      ) : (
        <input type={item.type || 'text'} style={commonStyle} placeholder={item.placeholder || ''} />
      )}
    </label>
  );
};

const CustomBlock = ({ item, theme = {} }) => {
  const imageUrl = getImageUrl(item);
  const type = String(item.type || item.icon || '').toLowerCase();
  if (type.includes('dropdown')) {
    return (
      <details style={{ border: `1px solid ${theme.borderColor || '#e5e7eb'}`, borderRadius: theme.cardRadius || 16, background: theme.surface || '#ffffff', padding: 18 }}>
        <summary style={{ cursor: 'pointer', fontWeight: 800 }}>{item.title || item.label || 'Dropdown'}</summary>
        <p style={{ margin: '12px 0 0', color: theme.mutedColor || '#64748b', lineHeight: 1.65 }}>{item.description || item.body || ''}</p>
      </details>
    );
  }
  if (imageUrl || type.includes('image')) {
    return (
      <figure style={{ margin: 0, overflow: 'hidden', borderRadius: theme.cardRadius || 18, background: '#f3f4f6' }}>
        {imageUrl && <img src={imageUrl} alt={item.imageAlt || item.title || 'Custom image'} style={{ display: 'block', width: '100%', minHeight: item.height || 220, objectFit: item.imageFit || 'cover' }} />}
        {(item.title || item.description) && (
          <figcaption style={{ padding: 14 }}>
            {item.title && <strong>{item.title}</strong>}
            {item.description && <p style={{ margin: '6px 0 0', color: theme.mutedColor || '#64748b' }}>{item.description}</p>}
          </figcaption>
        )}
      </figure>
    );
  }
  return (
    <article style={{ border: `1px solid ${theme.borderColor || '#e5e7eb'}`, borderRadius: theme.cardRadius || 18, background: item.backgroundColor || theme.surface || '#ffffff', padding: item.padding || 22, boxShadow: item.boxShadow || theme.cardShadow || 'none' }}>
      {(item.title || item.label) && <h3 style={{ margin: '0 0 8px', color: item.titleColor || theme.color || '#111827' }}>{item.title || item.label}</h3>}
      {(item.description || item.body || item.text) && <p style={{ margin: 0, color: item.descriptionColor || theme.mutedColor || '#64748b', lineHeight: 1.65 }}>{item.description || item.body || item.text}</p>}
      {item.buttonLabel && <div style={{ marginTop: 16 }}><CustomButton action={{ label: item.buttonLabel, url: item.url, color: item.buttonColor, textColor: item.buttonTextColor }} theme={theme} /></div>}
    </article>
  );
};

const CustomBuilderSection = ({ section, theme = {} }) => {
  const content = section.content || {};
  const style = section.style || {};
  const layout = section.layout || {};
  const scopeId = `custom-builder-${section.sectionId || section.type || 'section'}`.replace(/[^a-zA-Z0-9_-]/g, '-');
  const html = sanitizeHtml(section.customHtml || content.html || content.bodyHtml || '');
  const css = useMemo(() => scopeCss(section.customCss || content.css || '', scopeId), [section.customCss, content.css, scopeId]);
  const blocks = getVisible([...(getBlocks(section, 'blocks') || []), ...(getBlocks(section, 'items') || [])]);
  const fields = getVisible(content.fields);
  const actions = getActions(section).filter((item) => item?.label);
  const align = layout.align || style.textAlign || 'left';
  const maxWidth = layout.width === 'full' ? 'none' : style.maxWidth || content.maxWidth || theme.pageWidth || '1120px';

  // Emit the section's background / text color / padding as OVERRIDABLE stylesheet
  // defaults (at the section-root selector) instead of inline styles. Inline styles beat
  // a stylesheet, so authored Custom CSS (which targets the same root via `&` / `body`)
  // could never change the overall background/color/padding. As a stylesheet rule placed
  // BEFORE the custom CSS, the author's CSS wins by source order — so overall background,
  // font and alignment now apply. Structured "Background & size" edits still work because
  // they feed these same default values.
  const defaultBg = style.background || style.backgroundColor || theme.background || '#ffffff';
  const defaultColor = style.color || theme.color || '#111827';
  const defaultPadding = style.padding || theme.sectionSpacing || '72px 24px';
  const rootDefaultsCss = `#${scopeId}{background:${defaultBg};color:${defaultColor};padding:${defaultPadding};${style.minHeight ? `min-height:${style.minHeight};` : ''}}`;

  // Inline root style keeps only NON-visual structured props (radius, shadow, etc.);
  // background/color/padding are handled by the overridable rule above.
  const inlineRootStyle = (() => {
    const s = { ...toStyle(style) };
    delete s.background;
    delete s.backgroundColor;
    delete s.color;
    delete s.padding;
    return s;
  })();

  return (
    <section id={scopeId} data-custom-builder-section="true" style={inlineRootStyle}>
      <style>{rootDefaultsCss}</style>
      {css && <style>{css}</style>}
      <div style={{ maxWidth, margin: '0 auto', textAlign: align }}>
        {html ? <div dangerouslySetInnerHTML={{ __html: html }} /> : null}
        {!html && (content.eyebrow || content.title || content.subtitle) && (
          <div style={{ marginBottom: blocks.length || fields.length || actions.length ? 28 : 0 }}>
            {content.eyebrow && <p style={{ margin: '0 0 10px', color: style.accentColor || theme.primary || '#07111f', fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase' }}>{content.eyebrow}</p>}
            {content.title && <h2 style={{ margin: '0 0 12px', fontSize: 'clamp(32px, 5vw, 56px)', lineHeight: 1.05 }}>{content.title}</h2>}
            {content.subtitle && <p style={{ maxWidth: align === 'center' ? 760 : 680, margin: align === 'center' ? '0 auto' : 0, color: theme.mutedColor || '#64748b', lineHeight: 1.7 }}>{content.subtitle}</p>}
          </div>
        )}
        {blocks.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: content.gridColumns || 'repeat(auto-fit, minmax(220px, 1fr))', gap: style.gap || theme.gridGap || 18, textAlign: 'left' }}>
            {blocks.map((item, index) => <CustomBlock key={`${item.title || item.label || index}-${index}`} item={item} theme={theme} />)}
          </div>
        )}
        {fields.length > 0 && (
          <form style={{ display: 'grid', gridTemplateColumns: content.formColumns || 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginTop: blocks.length ? 24 : 0, textAlign: 'left' }} onSubmit={(event) => event.preventDefault()}>
            {fields.map((item, index) => <CustomField key={`${item.label || index}-${index}`} item={item} theme={theme} />)}
          </form>
        )}
        {actions.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start', marginTop: 24 }}>
            {actions.map((action, index) => <CustomButton key={`${action.label}-${index}`} action={action} theme={theme} />)}
          </div>
        )}
        {/* No authoring placeholder on the published reader — an empty section simply
            provides its (transparent) canvas area for overlay items. */}
      </div>
    </section>
  );
};

export default CustomBuilderSection;
